import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError } from "../api/client";

export type OrgRole = "org_admin" | "staff";

export interface AuthUser {
  id: string;
  email: string;
  fullName: string | null;
  /** This account has a confirmed second factor. */
  mfaEnabled: boolean;
  /** The server has MFA_SECRET_KEY set, so two-factor can be offered at all. */
  mfaAvailable: boolean;
  mfaBackupCodesRemaining: number;
  /** When the Terms of Use were accepted, or null if they haven't been. */
  termsAcceptedAt: string | null;
  /** Global operator access — reveals the /admin surface. */
  isPlatformAdmin: boolean;
  /** The org the user is currently acting within, or null for personal/family. */
  currentOrganizationId: string | null;
  /** The user's role in the current org, or null when in personal context. */
  role: OrgRole | null;
  createdAt: string;
  updatedAt: string;
}

interface RegisterVars {
  email: string;
  password: string;
  fullName: string;
}

interface LoginVars {
  email: string;
  password: string;
}

const AUTH_USER_KEY = ["auth", "user"] as const;

export function useCurrentUser() {
  return useQuery<AuthUser | null>({
    queryKey: AUTH_USER_KEY,
    queryFn: async () => {
      try {
        return await apiClient<AuthUser>("/api/auth/user");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation<AuthUser, ApiError, RegisterVars>({
    mutationFn: (vars) =>
      apiClient<AuthUser>("/api/auth/register", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: (user) => {
      // A freshly registered user is always in the personal context.
      queryClient.setQueryData(AUTH_USER_KEY, {
        ...user,
        currentOrganizationId: null,
        role: null,
      });
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
    },
  });
}

/**
 * A login either completes, or stops and asks for a second factor. The server
 * returns `{ mfaRequired: true }` INSTEAD of a user in the second case, so the
 * caller must narrow before treating the result as an identity.
 */
export type LoginResult = AuthUser | { mfaRequired: true };

export function isMfaChallenge(
  result: LoginResult,
): result is { mfaRequired: true } {
  return "mfaRequired" in result && result.mfaRequired === true;
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<LoginResult, ApiError, LoginVars>({
    mutationFn: (vars) =>
      apiClient<LoginResult>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: (result) => {
      // A pending MFA challenge is NOT an authenticated session — writing it to
      // the auth cache would make RequireAuth think the user is signed in.
      if (isMfaChallenge(result)) return;
      queryClient.setQueryData(AUTH_USER_KEY, {
        ...result,
        currentOrganizationId: null,
        role: null,
      });
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
    },
  });
}

/** Completes a login that stopped at the MFA challenge. */
export function useVerifyMfa() {
  const queryClient = useQueryClient();
  return useMutation<AuthUser, ApiError, { code: string }>({
    mutationFn: (vars) =>
      apiClient<AuthUser>("/api/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: (user) => {
      queryClient.setQueryData(AUTH_USER_KEY, {
        ...user,
        currentOrganizationId: null,
        role: null,
      });
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
    },
  });
}

export function useStartMfaSetup() {
  return useMutation<{ secret: string; otpauthUri: string }, ApiError, void>({
    mutationFn: () =>
      apiClient<{ secret: string; otpauthUri: string }>("/api/auth/mfa/setup", {
        method: "POST",
      }),
  });
}

export function useEnableMfa() {
  const queryClient = useQueryClient();
  return useMutation<{ backupCodes: string[] }, ApiError, { code: string }>({
    mutationFn: (vars) =>
      apiClient<{ backupCodes: string[] }>("/api/auth/mfa/enable", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
    },
  });
}

export function useDisableMfa() {
  const queryClient = useQueryClient();
  return useMutation<{ disabled: true }, ApiError, { password: string }>({
    mutationFn: (vars) =>
      apiClient<{ disabled: true }>("/api/auth/mfa/disable", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_USER_KEY });
    },
  });
}

export function useAcceptTerms() {
  const queryClient = useQueryClient();
  return useMutation<{ termsAcceptedAt: string }, ApiError, void>({
    mutationFn: () =>
      apiClient<{ termsAcceptedAt: string }>("/api/auth/accept-terms", { method: "POST" }),
    onSuccess: (data) => {
      // Patch the cached user rather than refetching: the gate unmounts the
      // moment termsAcceptedAt is non-null, so a round trip would leave the
      // modal up for no reason.
      queryClient.setQueryData<AuthUser | null>(AUTH_USER_KEY, (prev) =>
        prev ? { ...prev, termsAcceptedAt: data.termsAcceptedAt } : prev,
      );
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<{ loggedOut: true }, ApiError, void>({
    mutationFn: () =>
      apiClient<{ loggedOut: true }>("/api/auth/logout", { method: "POST" }),
    onSuccess: () => {
      queryClient.setQueryData(AUTH_USER_KEY, null);
      queryClient.clear();
    },
  });
}
